import React, { memo, useRef, useState, useCallback, useEffect } from 'react';

import { useSelector, useDispatch } from 'react-redux';
import KeepAlive, { useActivate, useUnactivate, useAliveController } from 'react-activation';
import { createSelector } from 'reselect';
import { message } from 'antd';

import styles from './Download.module.less';
import Toast from '../../../components/Toast/Toast';
import { PCStoreJsBridge } from '../../../nativeapi';
import {
  setOtherInfo,
  setQuietInstallPath,
  setUpdateNotifyStatus,
  setResidualNotifyStatus,
  setRarelyNotifyStatus,
} from '../../../actions/native';
import { logging } from '../../../actions/log';
import { setDownloadPathVisible, setIsEnabledSilent } from '../../../actions/layout';
import { cacheKeys } from '../../../constants/storageKeys';
import WallpaperPath from './WallpaperPath';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectOtherInfo = createSelector(
  state => state.native,
  native => native.otherInfo,
);

const selectQuietInstallPath = createSelector(
  state => state.native,
  native => native.quietInstallPath,
);

export default memo(() => {
  const dispatch = useDispatch();
  const inputRef = useRef();
  const otherInfo = useSelector(selectOtherInfo);
  const [pathValue, setPathValue] = useState('');
  const logInfoData = useSelector(selectLogInfo);
  const quietInstallPath = useSelector(selectQuietInstallPath);
  const [isEnabled, setIsEnabled] = useState(true);
  const [pathValueQuiet, setPathValueQuiet] = useState('');
  const inputRefQuiet = useRef();
  let clearSpanDay = 7;
  // 是否clearSpanDay天自动清除
  const [isEnabledClear, setIsEnabledClear] = useState(false);

  useEffect(() => {
    const startInfo = JSON.parse(localStorage.getItem(cacheKeys.START_INFO) || '{}');
    const enabled = localStorage.getItem(cacheKeys.ENABLE_QUICK_INSTALL) || '1';
    setIsEnabled(enabled === '' || enabled === '1');
    clearSpanDay = startInfo.spanday;

    // 获取初始化的自动删除安装包状态
    setIsEnabledClear(startInfo.autoclean === 1);
  }, []);

  useActivate(() => {
    PCStoreJsBridge.getOtherInfo().then(res => {
      dispatch(setOtherInfo(res));
    });
  });

  // 初始化 拿到下载路径
  useEffect(() => {
    if (otherInfo) {
      setPathValue(otherInfo.DownLoadPath);
    }
  }, [otherInfo]);

  // 初始化 拿到静默安装路径
  useEffect(() => {
    if (quietInstallPath) {
      setPathValueQuiet(quietInstallPath.installpath);
    }
  }, [quietInstallPath]);

  // 选择下载路径
  const handleBrowserFolder = useCallback(() => {
    const path = otherInfo.DownLoadPath || otherInfo.DefaultPath;
    PCStoreJsBridge.browserFolder(path).then(res => {
      setPathValue(res.DownLoadPath);
      setTimeout(() => {
        submitDownloadPath();
      }, 100);
    });

    // 点击 打开选择下载文件路径
    dispatch(
      logging({
        category: 'C',
        action: 'Check_Path',
        label: '',
        param2: {
          source: 'Setting',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          type: '0',
        },
      }),
    );
  }, [dispatch, otherInfo]);

  // 打开某个路径
  const handleOpenFolder = useCallback(
    (path, type) => {
      PCStoreJsBridge.openFolder(path);
      // 点击 打开文件路径
      dispatch(
        logging({
          category: 'C',
          action: 'Open_Path',
          label: '',
          param2: {
            source: 'Setting',
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            type, // 0: 普通安装  1：静默安装
          },
        }),
      );
    },
    [dispatch, logInfoData],
  );

  // 设置下载路径
  const submitDownloadPath = useCallback(() => {
    const matchStrD = /^[(\u4e00-\u9fa5)|(0-9)|(a-z)|(A-Z)|(_)|( )|(\\)|(:)]+$/;
    // 设置下载路径
    const DownLoadPath = inputRef.current.value;
    if (!DownLoadPath.match(matchStrD)) {
      message.info('下载目录，请不要选择有特殊字符的文件夹');
      return;
    }
    // 设置下载路径
    PCStoreJsBridge.setDownloadPath(DownLoadPath).then(
      data => {
        if (data.code === 0) {
          dispatch(setOtherInfo({ DownLoadPath }));
          Toast.success({
            content: '下载软件路径，设置成功！',
          });
        } else {
          let msg = '下载路径设置失败！';
          if (data.code === 4) {
            msg = '下载软件路径，盘符不存在！';
          } else if (data.code === 1) {
            msg = '下载软件路径，磁盘空间不足！';
          }
          setPathValue(otherInfo.DefaultPath);
          Toast.error({ content: msg });
        }

        // 点击 打开选择下载文件路径
        dispatch(
          logging({
            category: 'R',
            action: 'Check_Path',
            label: '',
            param2: {
              source: 'Setting',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              type: '0', // 0: 普通安装  1：静默安装  2:壁纸下载路径
              status: data.code === 0,
            },
          }),
        );
      },
      err => {
        setPathValue(otherInfo.DefaultPath);
        Toast.error({
          content: '下载路径设置失败！',
        });
      },
    );
  }, [inputRef, otherInfo]);

  // 恢复下载路径
  const handleRecover = useCallback(() => {
    const DownLoadPath = otherInfo.DefaultPath;
    setPathValue(DownLoadPath);
    setTimeout(() => {
      submitDownloadPath();
    }, 100);

    // 点击 恢复默认文件路径
    dispatch(
      logging({
        category: 'C',
        action: 'Recover_Path',
        label: '',
        param2: {
          source: 'Setting',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          type: '0', // 0: 普通安装  1：静默安装
        },
      }),
    );
  }, [dispatch, logInfoData]);

  // 选择是否打开静默安装
  const handleCheck = useCallback(enabled => {
    setIsEnabled(!enabled);

    // 设置是否取消快装
    localStorage.setItem(cacheKeys.ENABLE_QUICK_INSTALL, !enabled ? '1' : '0');
    dispatch(setIsEnabledSilent(!enabled));
    Toast.success({
      content: '设置成功!',
    });

    // 点击 切换打开快装
    dispatch(
      logging({
        category: 'C',
        action: 'Xpress_Cancel',
        label: '',
        param2: {
          source: 'Setting',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          enabled: !enabled,
        },
      }),
    );
  }, []);

  // 设置静默安装路径
  const submitQuietInstallPath = useCallback(() => {
    const matchStrI = /^[(\u4e00-\u9fa5)|(0-9)|(a-z)|(A-Z)|(_)|(\\)|(:)]+$/;
    // 设置安装路径
    const installpath = inputRefQuiet.current.value;
    if (!installpath.match(matchStrI)) {
      message.info('快装目录，请不要选择有空格或特殊字符的文件夹');
      return;
    }
    PCStoreJsBridge.setQuietInstallPath(installpath).then(
      data => {
        if (data.code === 0) {
          // 设置快装路径
          dispatch(
            setQuietInstallPath({
              ...quietInstallPath,
              installpath,
            }),
          );
          Toast.success({
            content: '软件静默安装路径，设置成功！',
          });
        } else {
          let msg = '软件静默安装路径，设置失败！';
          if (data.code === 4) {
            msg = '软件静默安装路径，盘符不存在！';
          } else if (data.code === 1) {
            msg = '软件静默安装路径，磁盘空间不足！';
          }
          setPathValueQuiet(quietInstallPath.installpath);
          Toast.error({ content: msg });
        }

        // 点击 打开选择下载文件路径
        dispatch(
          logging({
            category: 'R',
            action: 'Check_Path',
            label: '',
            param2: {
              source: 'Setting',
              source_p: logInfoData.source_p,
              source_pp: logInfoData.source_pp,
              type: '1', // 0: 普通安装  1：静默安装  2:壁纸下载路径
              status: data.code === 0,
            },
          }),
        );
      },
      err => {
        Toast.error({
          content: '软件静默安装路径，设置失败！',
        });
        setPathValueQuiet(quietInstallPath.installpath);
      },
    );
  }, [inputRefQuiet, quietInstallPath]);

  // 选择静默安装路径
  const handleBrowserFolderQuiet = useCallback(() => {
    const path = quietInstallPath.installpath || '';
    PCStoreJsBridge.browserFolder(path).then(res => {
      setPathValueQuiet(res.DownLoadPath);
      setTimeout(() => {
        submitQuietInstallPath();
      }, 100);
    });

    // 点击 打开选择文件路径
    dispatch(
      logging({
        category: 'C',
        action: 'Check_Path',
        label: '',
        param2: {
          source: 'Setting',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          type: '1', // 0: 普通安装  1：静默安装
        },
      }),
    );
  }, [dispatch, quietInstallPath]);

  // 点击 恢复默认的静默安装路径
  const handleRecoverQuiet = useCallback(() => {
    const definstallpath = quietInstallPath.definstallpath || 'D:\\AppStoreInstalled\\';
    setPathValueQuiet(definstallpath);
    setTimeout(() => {
      submitQuietInstallPath();
    }, 100);

    // 点击 恢复默认文件路径
    dispatch(
      logging({
        category: 'C',
        action: 'Recover_Path',
        label: '',
        param2: {
          source: 'Setting',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          type: '1', // 0: 普通安装  1：静默安装
        },
      }),
    );
  }, [dispatch, logInfoData]);

  const handleCheckClear = useCallback(enabled => {
    setIsEnabledClear(!enabled);
    Toast.success({
      content: '设置成功!',
    });

    // 保存自动清除安装包
    const param = {
      autoclean: !enabled ? 1 : 0,
      spanday: clearSpanDay,
    };
    PCStoreJsBridge.settingAutoClearApp(param).then(ret2 => {
      if (ret2.succ === 'true') {
        const startInfo = JSON.parse(localStorage.getItem(cacheKeys.START_INFO) || '{}');
        localStorage.setItem(cacheKeys.START_INFO, JSON.stringify({ ...startInfo, ...param }));
      } else {
        message.info('自动删除安装包设置失败');
      }
    });

    // 点击 切换打开快装
    dispatch(
      logging({
        category: 'C',
        action: 'ClearApp_Cancel',
        label: '',
        param2: {
          source: 'Setting',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          enabled: !enabled,
        },
      }),
    );
  }, []);

  return (
    <div className={styles.s_download}>
      <div className="s_line">
        <div className="s_select_title">下载路径设置：</div>
        <div className="s_content">
          <div className="desc">将下载的软件保存在以下目录：</div>
          <div className="choose">
            <input
              type="text"
              className="value text-ellipsis"
              readOnly
              ref={inputRef}
              value={pathValue}
            />
            <div className="btn" onClick={handleBrowserFolder}>
              选择目录
            </div>
            <div
              className="open"
              onClick={() => {
                handleOpenFolder(pathValue, '0');
              }}
            >
              打开
            </div>
          </div>
          <div className="recovery" onClick={handleRecover}>
            恢复默认目录
          </div>
        </div>
      </div>
      <div className="s_line">
        <div className="s_select_title">快装设置:</div>
        <div className="s_content">
          <div className="checkbox" onClick={() => handleCheck(isEnabled)}>
            {isEnabled ? <div className="checked" /> : <div className="no_checked" />}
            <span>开启快装功能</span>
          </div>
          {!isEnabled && <div className="div_disable_cover" />}
          <div className={isEnabled ? styles.div_enable : styles.div_disable}>
            <div className="desc">将支持“快装”（一键静默安装）的软件默认安装到以下目录：</div>
            <div className="choose">
              <input
                type="text"
                className="value text-ellipsis"
                readOnly
                ref={inputRefQuiet}
                value={pathValueQuiet}
              />
              <div className="btn" onClick={handleBrowserFolderQuiet}>
                选择目录
              </div>
              <div
                className="open"
                onClick={() => {
                  handleOpenFolder(pathValueQuiet, '1');
                }}
              >
                打开
              </div>
            </div>
            <div className="recovery" onClick={handleRecoverQuiet}>
              恢复默认目录
            </div>
          </div>
        </div>
      </div>
      <WallpaperPath />
      <div className="s_line">
        <div className="s_select_title">安装包清理:</div>
        <div className="s_content">
          <div className="checkbox checkbox_clear" onClick={() => handleCheckClear(isEnabledClear)}>
            {isEnabledClear ? <div className="checked" /> : <div className="no_checked" />}
            <span>下载完成{clearSpanDay}天后，自动删除安装包</span>
          </div>
        </div>
      </div>
    </div>
  );
});
